<template>
	<div class="report-form">
		<div class="report-form-row-2">
			<ja-dropdown
				v-model="simulationType"
				required
				:placeholder="$t('simTypePlaceholder')"
				:label="$t('simulationTypeLbl')"
				:options="simulationTypes"
				name="simulationType"
			></ja-dropdown>
			<div>
				<ja-date-input
					v-model="dateInput"
					:disabled="simulationType < 0"
					:placeholder="$t('startDate')"
					:label="$t('selectStartDate')"
					:max-date="maxStartDate"
					name="startDate"
				></ja-date-input>
			</div>
		</div>
		<div class="report-form-row-2">
			<ja-input
				v-model="schoolInput"
				name="school"
				type="text"
				:label="$t('schoolName')"
				:placeholder="$t('schoolNamePlaceholder')"
				:disabled="selectedFacilityId <= 0"
				escape-string
				trim-spaces
				:autocomplete-options="schools"
				@optionSelected="onSchoolSelected"
			></ja-input>
			<ja-input
				v-model="educatorInput"
				name="simEducator"
				:disabled="selectedFacilityId <= 0 || !!educatorName"
				:label="$t('educatorName')"
				:placeholder="
					educatorName ? educatorName : $t('educatorNamePlaceholder')
				"
				:autocomplete-options="educators"
				escape-string
				trim-spaces
				@optionSelected="onEducatorSelected"
			></ja-input>
		</div>
		<div class="report-form-row-2">
			<ja-dropdown
				v-model="simulationClass"
				:disabled="isClassFieldDisabled"
				:placeholder="$t('classNamePlaceholder')"
				name="simulationClass"
				:label="$t('className')"
				:options="simulationClasses"
			></ja-dropdown>
			<ja-input
				v-model="studentInput"
				name="student"
				:disabled="isStudentFieldDisabled"
				:label="$t('studentName')"
				:placeholder="$t('studentNamePlaceholder')"
				:autocomplete-options="students"
				:show-clear-btn="isStudentSelected"
				escape-string
				trim-spaces
				@optionSelected="onStudentSelected"
				@onClearInput="handlerClearInput"
			></ja-input>
		</div>
	</div>
</template>
<script>
import gql from 'graphql-tag';
import { debounce } from '@/helpers/utils';
import ReportingMixin from '@/mixins/ReportingMixin';
import DateFormat from '@/mixins/DateFormat';
import AdminFacility from '@/mixins/AdminFacility';
import Input from '@/components/ui/form/Input';
import DateInput from '@/components/ui/form/DateInput';
import Dropdown from '@/components/ui/form/Dropdown';
export default {
	components: {
		jaInput: Input,
		jaDropdown: Dropdown,
		jaDateInput: DateInput
	},
	mixins: [ReportingMixin, DateFormat, AdminFacility],
	props: {
		filters: {
			type: Object,
			default: null
		},
		simulationTypes: {
			type: Array,
			default: () => {
				return [];
			}
		},
		lifeStages: {
			type: Array,
			default: () => {
				return [];
			}
		},
		educatorName: {
			type: String,
			default: ''
		},
		clear: {
			type: Boolean,
			default: true
		}
	},
	data() {
		return {
			dateInput: null,
			student: null,
			studentInput: '',
			students: [],
			debounceSearchStudents: null,
			isStudentSelected: false
		};
	},
	computed: {
		activeFilters() {
			if (this.filters) {
				return this.filters;
			}

			return {
				facility: this.selectedFacilityId,
				simulationType: null,
				school: null,
				educator: null,
				simulationClass: null,
				student: null,
				startDate: null,
				endDate: null,
				visitStartDate: null,
				visitEndDate: null
			};
		},
		isClassFieldDisabled() {
			if (this.selectedFacilityId <= 0) {
				return true;
			}
			if (this.school || this.educator) {
				return false;
			}

			return true;
		},
		isStudentFieldDisabled() {
			if (this.isStudentSelected) {
				return true;
			}
			if (this.selectedFacilityId <= 0) {
				return true;
			}
			if (this.school || this.educator) {
				return false;
			}
			return true;
		},
		requiredFieldsPopulated() {
			return this.selectedFacilityId >= 0 && this.simulationType >= 0;
		},
		maxStartDate() {
			return new Date();
		}
	},
	watch: {
		clear(value) {
			if (value === true) {
				this.facilityChanged();
			}
		},
		studentInput(value) {
			if (value.length >= this.minLettersInput) {
				this.debounceSearchStudents(value);
			} else {
				this.student = '';
				this.students = [];
			}
		},
		dateInput(newDate) {
			if (newDate != null) {
				if (this.simulationType >= 0) {
					const startDate = this.formatStartDate(newDate);
					const endDate = this.formatEndDate(newDate);
					if (
						this.simulationTypes[this.simulationType].value == 'AdvancedOnsite'
					) {
						this.activeFilters.startDate = startDate;
						this.activeFilters.visitStartDate = startDate;
						this.activeFilters.visitEndDate = endDate;
					} else {
						this.activeFilters.startDate = startDate;
						this.activeFilters.endDate = endDate;
					}
					this.$emit('filtersUpdated', this.activeFilters);
				}
			} else {
				this.activeFilters.visitStartDate = null;
				this.activeFilters.visitEndDate = null;
				this.activeFilters.startDate = null;
				this.activeFilters.endDate = null;
				this.$emit('filtersUpdated', this.activeFilters);
			}
		},
		simulationType(newValue) {
			if (newValue > -1) {
				this.activeFilters.simulationType = this.simulationTypes[
					newValue
				].value;
				if (this.activeFilters.simulationType == 'AdvancedOnsite') {
					this.activeFilters.visitStartDate = this.formatStartDate(
						this.dateInput
					);
					this.activeFilters.visitEndDate = this.formatEndDate(this.dateInput);
					this.activeFilters.startDate = null;
					this.activeFilters.endDate = null;
				} else {
					this.activeFilters.visitStartDate = null;
					this.activeFilters.visitEndDate = null;
					this.activeFilters.startDate = this.formatStartDate(this.dateInput);
					this.activeFilters.endDate = this.formatEndDate(this.dateInput);
				}
			} else {
				this.activeFilters.simulationType = null;
			}

			this.$emit('filtersUpdated', this.activeFilters);
		},
		school(newValue) {
			this.activeFilters.school = newValue;
			this.simulationClass = -1;

			this.$emit('filtersUpdated', this.activeFilters);
			if (this.facility >= 0) {
				this.getSimulationClasses();
			}
		},
		educator(newValue) {
			this.activeFilters.educator = newValue;

			this.$emit('filtersUpdated', this.activeFilters);
			if (this.facility >= 0) {
				this.getSimulationClasses();
			}
		},
		student(newValue) {
			this.activeFilters.student = newValue;

			this.$emit('filtersUpdated', this.activeFilters);
		},
		simulationClass(newValue) {
			if (newValue >= 0) {
				this.activeFilters.simulationClass = this.simulationClasses[
					newValue
				].value;
			} else {
				this.activeFilters.simulationClass = null;
			}

			this.$emit('filtersUpdated', this.activeFilters);
		}
	},
	created() {
		this.debounceSearchStudents = debounce(value => {
			this.student = value;
			this.getStudents();
		}, this.debounceTime);
	},
	mounted() {
		if (this.educatorName) {
			this.educator = this.educatorName;
		}
		if (this.filters) {
			if (this.filters.student) {
				this.studentInput = this.filters.student;
			}

			if (this.filters.startDate) {
				this.dateInput = new Date(this.filters.startDate);
			} else if (this.filters.visitStartDate) {
				this.dateInput = new Date(this.filters.visitStartDate);
			}
		}
		if (this.studentInput) {
			this.isStudentSelected = true;
		}
	},
	methods: {
		async getStudents() {
			if (this.studentInput.length >= this.minLettersInput) {
				const filters = [];
				const studentNames = this.studentInput.split(' ');

				if (studentNames.length == 1) {
					filters.push(
						`((firstName.Contains("${this.studentInput}")) || (lastName.Contains("${this.studentInput}")))`
					);
				} else {
					filters.push(
						`((firstName.Contains("${studentNames[0]}")) && (lastName.Contains("${studentNames[1]}")))`
					);
				}

				if (this.school) {
					filters.push(`simulation.school.Contains("${this.school}")`);
				}
				if (this.educator) {
					const educatorNames = this.educator.split(' ');

					if (educatorNames.length == 1) {
						filters.push(
							`simulation.UserSimulations.Count(it.User.firstName.Contains("${this.educator}") || it.User.lastName.Contains("${this.educator}")) > 0`
						);
					} else {
						filters.push(
							`simulation.UserSimulations.Count(it.User.firstName.Contains("${educatorNames[0]}") && it.User.lastName.Contains("${educatorNames[1]}")) > 0`
						);
					}
				}

				const response = await this.$apollo.query({
					query: gql`
						query Students($where: String!) {
							students(uniquefield: "new {firstName,lastName}", where: $where) {
								id
								firstName
								lastName
							}
						}
					`,

					variables: {
						where: filters.join('&&')
					},
					fetchPolicy: 'no-cache'
				});

				if (response.data && response.data.students) {
					this.students = [];
					response.data.students.forEach(user => {
						this.students.push(`${user.firstName} ${user.lastName}`);
					});
				}
			}
		},
		facilityChanged() {
			this.simulationType = -1;
			this.dateInput = null;
			this.schoolInput = '';
			this.educatorInput = '';
			this.studentInput = '';
			if (this.simulationClass) {
				this.simulationClass = -1;
			}

			this.educators = [];
			this.schools = [];
			this.students = [];
		},
		onStudentSelected(student) {
			this.student = student;
			this.students = [];
			this.studentInput = student;
			this.isStudentSelected = true;
		},
		handlerClearInput() {
			this.studentInput = '';
			this.isStudentSelected = false;
		}
	}
};
</script>
